// ScheduleViewPage.tsx
import React, { useState, useEffect } from 'react';
import { Grid, Typography, Box, IconButton, Paper } from '@mui/material';
import { MoonLoader } from 'react-spinners';
import { Fullscreen, FullscreenExit, Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';

import { apiGetScheduleView } from '../../services/apiService';
// substituímos import de getMonthBoundaryDate por nossa nova util
import { getBoundaryDates45 } from './getBoundaryDates45';
import Filters from '../../components/ScheduleView/Filters/Filters';
import ScheduleViewTable from '../../components/ScheduleView/ScheduleViewTable/ScheduleViewTable';
import WorkerBoardingReport from '../../components/ScheduleView/WorkerBoardingReport';

const ScheduleViewPage: React.FC = () => {
	// obtém as datas padrões (primeiro dia do mês atual e primeiro dia do mês após +45 dias)
	const { start: defaultStart, end: defaultEnd } = getBoundaryDates45();

	const [data, setData] = useState<any[]>([]);
	const [days, setDays] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

	// inicializa com nossas datas padrão em vez de getMonthBoundaryDate
	const [startDate, setStartDate] = useState<string>(defaultStart);
	const [endDate, setEndDate] = useState<string>(defaultEnd);

	// busca de dados
	const fetchData = async (
		sDate: string,
		eDate: string,
		client = '',
		platform = '',
		project = '',
		scope = '',
		classification = '',
		coordinator = '',
		opRoles: number[] = [],
		workersIds: number[] = [],
		skillIds: number[] = [],
		eventAcronyms: string[] = [],
		contractCats: number[] = [],
		activeWorkersOnly = true
	) => {
		setIsLoading(true);
		try {
			const resp = await apiGetScheduleView(
				startDate,
				endDate,
				'',
				'',
				'',
				'',
				'',
				'', // filtros vazios
				[],
				[],
				[],
				[],
				[], // listas vazias
				true, // no_schedule
				true // active_workers_only
			);
			setData(resp.workers);
			setDays(resp.days);
		} catch (err) {
			console.error(err);
		} finally {
			setIsLoading(false);
		}
	};

	// carga inicial apenas uma vez
	useEffect(() => {
		fetchData(defaultStart, defaultEnd);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// wrapper fullscreen
	const fullScreenWrapperStyle: React.CSSProperties = isFullScreen
		? {
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 9999,
				backgroundColor: 'white',
				padding: 20,
				overflow: 'auto',
		  }
		: {};

	// exporta Excel
	const handleDownload = () => {
		if (!data.length) return;
		const dateCols = days.filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d));

		// NOVO HEADER: adicionadas as três colunas antes das datas
		const header = [
			'WO',
			'Cliente',
			'Plataforma',
			'Função',
			'Trabalhador',
			'Nº E',
			'Início',
			'Fim',
			...dateCols,
		];

		// agora a linha inclui boarding_number, scheduled_start_date e scheduled_end_date
		const rows = data.map(row => [
			row.WO,
			row.client_name,
			row.platform_name,
			row.operation_role,
			row.worker_name,
			row.boarding_number,
			row.scheduled_start_date,
			row.scheduled_end_date,
			...row.events.slice(0, dateCols.length),
		]);

		const aoa = [header, ...rows];
		const ws = XLSX.utils.aoa_to_sheet(aoa);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'ScheduleView');
		const filename = `schedule_view_${startDate}_to_${endDate}.xlsx`;
		XLSX.writeFile(wb, filename);
	};

	return (
		<>
			<Grid style={{ padding: 20, marginBottom: 30 }}>
				<Typography variant="h5" style={{ marginBottom: 40 }}>
					Visão de Programação
				</Typography>

				<Filters
					setScheduleViewData={({ workers, days }) => {
						setData(workers);
						setDays(days);
					}}
					setIsLoading={setIsLoading}
					startDate={startDate}
					setStartDate={setStartDate}
					endDate={endDate}
					setEndDate={setEndDate}
				/>

				<Box
					style={{
						marginTop: isFullScreen ? 20 : 0,
						...fullScreenWrapperStyle,
					}}
				>
					<Box display="flex" justifyContent="flex-end" gap="8px" mb={2}>
						<IconButton
							onClick={handleDownload}
							style={{
								backgroundColor: '#007eb8',
								color: 'white',
								width: 30,
								height: 30,
								padding: 0,
								borderRadius: 5,
							}}
						>
							<Download />
						</IconButton>
						<IconButton
							onClick={() => setIsFullScreen(!isFullScreen)}
							style={{
								backgroundColor: '#007eb8',
								color: 'white',
								width: 30,
								height: 30,
								padding: 0,
								borderRadius: 5,
							}}
						>
							{isFullScreen ? <FullscreenExit /> : <Fullscreen />}
						</IconButton>
					</Box>

					{isLoading ? (
						<MoonLoader color="#36d7b7" />
					) : (
						<ScheduleViewTable
							data={data}
							days={days}
							isFullScreen={isFullScreen}
						/>
					)}
				</Box>
			</Grid>
			<WorkerBoardingReport />
		</>
	);
};

export default ScheduleViewPage;
